/* istanbul ignore file */
import React from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { CampaignSubTypeMapping, isXandrDisplayAdsCampaign, isXandrVideoAdsCampaign, isAudienceNativeAdsCampaign, isSearchCampaign } from '@bingads-webui-campaign-react/constants';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { ProgressiveLoadingBlock } from '@bingads-webui-campaign-react/shell-ui-common';
import { setClarityTag } from '@bingads-webui-universal/clarity';

import { withCampaign, withCreationHeader } from '../helper';

const getStyledAdGroupCreationSearchAsync = () => import(/* webpackMode: 'eager' */'./adgroup-creation-search')
  .then(({ StyledAdGroupCreationSearch }) => StyledAdGroupCreationSearch);
const getStyledAdGroupCreationAudienceAsync = () => import(/* webpackMode: 'eager' */'./adgroup-creation-audience')
  .then(({ StyledAdGroupCreationAudience }) => StyledAdGroupCreationAudience);
const getHotelAdGroupCreationAsync = () => import(/* webpackMode: 'eager' */'@bingads-webui-campaign/hotel-adgroup-upsert')
  .then(({ HotelAdGroupCreation }) => HotelAdGroupCreation);
const getXandrVideoAdGroupCreationAsync = () => import(/* webpackMode: 'eager' */ '@bingads-webui-campaign-react/xandr-video-campaign')
  .then(({ XandrVideoAdGroupCreationPage }) => XandrVideoAdGroupCreationPage);
const getXandrDisplayAdsAdGroupCreationAsync = () => import(/* webpackMode: 'eager' */ '@bingads-webui-campaign-react/xandr-display-ads-campaign')
  .then(({ XandrDisplayAdsAdGroupCreationPage }) => XandrDisplayAdsAdGroupCreationPage);
const getDisplayPlusAdGroupCreationAsync = () => import(/* webpackMode: 'eager' */ '@bingads-webui-campaign-react/display-plus-campaigns')
  .then(({ DisplayPlusCreateAdGroupWizard }) => DisplayPlusCreateAdGroupWizard);
const getSearchAdGroupCreationRevampAsync = () => import(/* webpackMode: 'eager' */ '@bingads-webui-campaign-react/search-campaign-wizard')
  .then(({ SearchAdGroupCreationWizard }) => SearchAdGroupCreationWizard);

/**
 * @param {Function} getComponentAsync a function that returns a lazy loading component
 * @param {Object} i18n i18n object
 * @returns {Function} a function that can be passed to React.lazy()
 */
const getComponent = (getComponentAsync, i18n) =>
  () => getComponentAsync()
    .then(component => ({ default: withCreationHeader(component, [i18n.getString(_TL_('Ad Groups')), i18n.getString(_TL_('Create ad group'))], 'adgroups') }));

class AdGroupCreation extends React.PureComponent {
  constructor(props) {
    super(props);

    const { scope, i18n } = props;
    this.SearchComponent = React.lazy(getComponent(getStyledAdGroupCreationSearchAsync, i18n));
    this.SearchRevampComponent = React.lazy(getComponent(getSearchAdGroupCreationRevampAsync, i18n));
    this.AudienceComponent = React.lazy(getComponent(getStyledAdGroupCreationAudienceAsync, i18n));
    this.HotelComponent = React.lazy(getComponent(getHotelAdGroupCreationAsync, i18n));
    this.XandrVideoComponent = React.lazy(getComponent(getXandrVideoAdGroupCreationAsync, i18n));
    this.XandrDisplayAdsComponent = React.lazy(getComponent(getXandrDisplayAdsAdGroupCreationAsync, i18n));
    this.DisplayPlusComponent = React.lazy(getComponent(getDisplayPlusAdGroupCreationAsync, i18n));

    this.isOpenCreatePanel = !!scope.values.isOpenCreatePanel;
    if (this.isOpenCreatePanel) {
      delete scope.values.isOpenCreatePanel;
      scope.history.replace({
        pathname: scope.history.location.pathname,
        search: scope.stringify(),
      });
    }
  }

  render() {
    const { permissions } = this.props;
    const campaignType = _.chain(this.props)
      .result('campaignContext')
      .result('currentCampaign')
      .result('CampaignType')
      .value();
    const campaignSubType = _.chain(this.props)
      .result('campaignContext')
      .result('currentCampaign')
      .result('CampaignSubType')
      .value();

    let Component = this.SearchComponent;

    if (isSearchCampaign(campaignType) && permissions && permissions.dynamic &&
    permissions.dynamic.SearchCampaignCreationRevamp && permissions.dynamic.SearchRevampPhase2) {
      Component = this.SearchRevampComponent;
    }

    if (campaignType === 'Audience') {
      Component = this.AudienceComponent;
      if (permissions && permissions.dynamic && permissions.dynamic.XandrVideoCampaigns &&
        isXandrVideoAdsCampaign(campaignSubType)) {
        if (permissions.dynamic.CTVAndDealToDisplayPlus && permissions.dynamic.DisplayPlusAdGroupCreation) {
          setClarityTag({
            IsDisplayPlusAdGroupCreationEnabled: true,
            CampaignSubType: campaignSubType,
          });
          Component = this.DisplayPlusComponent;
        } else {
          Component = this.XandrVideoComponent;
        }
      }
      if (permissions && permissions.dynamic && permissions.dynamic.XandrDisplayAdsCampaigns &&
        isXandrDisplayAdsCampaign(campaignSubType)) {
        Component = this.XandrDisplayAdsComponent;
      }
      if (permissions && permissions.dynamic && permissions.dynamic.DisplayPlusAdGroupCreation &&
        isAudienceNativeAdsCampaign(campaignSubType)) {
        setClarityTag({
          IsDisplayPlusAdGroupCreationEnabled: true,
          CampaignSubType: campaignSubType,
        });
        Component = this.DisplayPlusComponent;
      }
    }

    if (permissions && permissions.dynamic && permissions.dynamic.HotelCampaign && campaignType === 'Hotel') {
      Component = this.HotelComponent;
    }

    return (
      <React.Suspense fallback={<ProgressiveLoadingBlock />}>
        <Component {...this.props} isVideo={campaignSubType === CampaignSubTypeMapping.AudienceVideoAds} isOpenCreatePanel={this.isOpenCreatePanel} />
      </React.Suspense>
    );
  }
}

AdGroupCreation.propTypes = {
  campaignContext: PropTypes.shape({}).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      HotelCampaign: PropTypes.bool,
      XandrVideoCampaigns: PropTypes.bool,
      XandrDisplayAdsCampaigns: PropTypes.bool,
      DisplayPlusAdGroupCreation: PropTypes.bool,
      CTVAndDealToDisplayPlus: PropTypes.bool,
      SearchCampaignCreationRevamp: PropTypes.bool,
      SearchRevampPhase2: PropTypes.bool,
    }).isRequired,
  }).isRequired,
  scope: PropTypes.shape({
    values: PropTypes.shape({
      isOpenCreatePanel: PropTypes.bool,
    }),
    history: PropTypes.shape({
      replace: PropTypes.func,
      location: PropTypes.shape({
        pathname: PropTypes.string,
        search: PropTypes.string,
      }),
    }),
    stringify: PropTypes.func,
  }).isRequired,
};

export const AdGroupCreationWithCampaign = withCampaign(withI18nConsumer(AdGroupCreation));
